import React, { useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./location_code_wh_transfer_out.css";
import axios from 'axios';
import useSWR from 'swr';

interface DataType {
    key: string;
    locationCode: string;
    locationName: string;
}

interface ApiResponse {
    data: DataType[];
    total: number;
}

interface LocationCodeWhOutProps {
    warehouseCode: string | null;
    itemCode: string | null;
    onRowDoubleClick: (record: DataType) => void;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);
type DataIndex = keyof DataType;

const LocationCodeWhOut: React.FC<LocationCodeWhOutProps> = ({ warehouseCode, itemCode, onRowDoubleClick }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [sort, setSort] = useState<{ field?: string, order?: string }>({});
    const [filters, setFilters] = useState<Record<string, string>>({});

    const buildQueryString = () => {
        const params = new URLSearchParams();
        params.append('page', pagination.current.toString());
        params.append('pageSize', pagination.pageSize.toString());
        if (sort.field) {
            params.append('sortField', sort.field);
            params.append('sortOrder', sort.order || '');
        }
        Object.entries(filters).forEach(([key, val]) => {
            if (val) params.append(key, val);
        });
        return params.toString();
    };

    const { data, error, isLoading } = useSWR<ApiResponse>(
        warehouseCode && itemCode ? `${process.env.REACT_APP_BACKEND_URL}/api/location-codes/${warehouseCode}/${itemCode}?${buildQueryString()}` : null,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setFilters(prev => ({ ...prev, [dataIndex]: selectedKeys[0] }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setFilters(prev => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Location Code',
            dataIndex: 'locationCode',
            key: 'locationCode',
            ...getColumnSearchProps('locationCode'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Location Name',
            dataIndex: 'locationName',
            key: 'locationName',
            ...getColumnSearchProps('locationName'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i9pl">
                <div className="gjs-grid-column" id="iz5k">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data?.data}
                        loading={isLoading}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: data?.total || 0,
                            showSizeChanger: true,
                        }}
                        onChange={(newPagination, _, newSorter) => {
                            setPagination({
                                current: newPagination.current || 1,
                                pageSize: newPagination.pageSize || 10,
                            });
                            if (!Array.isArray(newSorter) && newSorter.field) {
                                setSort({
                                    field: newSorter.field as string,
                                    order: newSorter.order || undefined,
                                });
                            } else {
                                setSort({});
                            }
                        }}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => onRowDoubleClick(record),
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default LocationCodeWhOut;